<template>
   <div :class="col">
      <div ref="field" class="mb-0 position-relative sa-field sa-date-field">
         <!-- Label above the box (main.js formStyle 'icons' and 'plain') -->
         <label v-if="label && !floating && !compact" :for="inputId" class="form-label">{{ label }}</label>

         <div ref="box" :class="wrapClass" @focusin="focused = true" @focusout="focused = false">
            <!-- Icon in front (formStyle 'icons' only). Clicking it opens the calendar too -->
            <span v-if="showIcon" class="input-group-text sa-date-icon" @mousedown.prevent="toggle"><i :class="icon"></i></span>

            <!-- You can type the date (dd/mm/yyyy) or pick it from the calendar -->
            <input
               ref="input"
               v-bind="$attrs"
               :id="inputId"
               type="text"
               inputmode="numeric"
               autocomplete="off"
               :maxlength="month ? 7 : withTime ? 16 : 10"
               :value="text"
               :placeholder="floating ? ' ' : placeholderText"
               @click="open"
               @input="onType"
               @blur="onBlur"
               @keydown.down.prevent="open"
               @keydown.esc="close"
               @keydown.enter.prevent="onEnter"
               @keydown.tab="close"
               class="form-control custom-tooltip-input"
            />

            <!-- Floating label (formStyle 'floating') -->
            <label v-if="floating" :for="inputId" class="sa-float-label">{{ label }}</label>
         </div>

         <!-- The calendar, under the box (or above it when there's no room below). Uses the theme's dropdown
              colours (.dropdown-menu). It is moved to <body> so a card, table or window can't cut it off -->
         <Teleport to="body">
         <div v-if="isOpen" ref="menu" class="dropdown-menu show sa-date-menu" :style="menuStyle" @mousedown.prevent>
            <VueDatePicker
               :model-value="pickerValue"
               :model-type="month ? undefined : withTime ? 'yyyy-MM-dd HH:mm' : 'yyyy-MM-dd'"
               :month-picker="month"
               inline
               auto-apply
               :enable-time-picker="withTime && !month"
               :is-24="!ampm"
               :minutes-increment="minuteStep"
               :week-start="1"
               :min-date="minDate"
               :max-date="maxDate"
               :month-change-on-scroll="false"
               @update:model-value="pick"
            />
            <!-- Quick buttons -->
            <div class="d-flex justify-content-between px-2 pb-1">
               <button type="button" class="btn btn-link btn-sm text-decoration-none px-1" :disabled="todayBlocked" @click="pickToday">{{ month ? "This month" : withTime ? "Now" : "Today" }}</button>
               <span>
                  <button type="button" class="btn btn-link btn-sm text-decoration-none px-1" @click="clear">Clear</button>
                  <!-- With time: the calendar stays open while you set the time -->
                  <button v-if="withTime && !month" type="button" class="btn btn-primary btn-sm ms-1" @click="close">Done</button>
               </span>
            </div>
         </div>
         </Teleport>

         <small v-if="hint" class="text-muted d-block">{{ hint }}</small>

         <!-- Above the box: on focus, and in red while the field is invalid -->
         <div v-if="tooltip" class="custom-tooltip">{{ tooltip }}<span class="tooltip-arrow"></span></div>
      </div>
   </div>
</template>

<script>
// A date box with a themed calendar (@vuepic/vue-datepicker). Same looks as FormField (main.js formStyle).
// with-time: date and time. v-model = "YYYY-MM-DD HH:MM" (for a DATETIME column), the box shows dd/mm/yyyy hh:mm.
//   ampm: 12-hour clock (default 24-hour). minute-step: e.g. 5 or 15.
// month: a month only. v-model = "YYYY-MM", the box shows mm/yyyy, the calendar shows the 12 months.
//   <DateField v-model="formInputs.date" col="col-md-6" label="Date" icon="fa fa-calendar"
//              tooltip="Please choose a date" required @change="handleInputChange" />
// v-model = "YYYY-MM-DD" (what the server and the database use), or "" when empty.
// The box shows dd/mm/yyyy. You can type it (29/10/2026, also 29-10-2026 or 29.10.2026) or pick it.
// min / max: "today" or "YYYY-MM-DD" (days outside are greyed out, typed ones are refused).
// required, @input, @change ... go to the <input>; picking a day fires them too, so validation runs.
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";

let nextId = 0;

const pad = (n) => String(n).padStart(2, "0");

// Today as YYYY-MM-DD in the user's own time zone
function todayIso() {
   const d = new Date();
   return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
// Now as HH:MM
function nowTime() {
   const d = new Date();
   return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default {
   name: "DateField",
   components: { VueDatePicker },
   inheritAttrs: false,
   props: {
      modelValue: { default: "" },
      label: { type: String, default: "" },
      icon: { type: String, default: "fa fa-calendar" },
      placeholder: { type: String, default: "" },  // empty = "dd/mm/yyyy" (or "dd/mm/yyyy hh:mm" with time)
      tooltip: { type: String, default: "" },
      hint: { type: String, default: "" },
      col: { type: String, default: "col-md-6" },
      id: { type: String, default: "" },
      compact: { type: Boolean, default: false },
      min: { type: String, default: "" }, // "today" or "YYYY-MM-DD"
      max: { type: String, default: "" },
      withTime: { type: Boolean, default: false }, // date + time
      ampm: { type: Boolean, default: false },     // 12-hour clock in the calendar
      minuteStep: { type: Number, default: 1 },    // minutes in steps of 1, 5, 15 ...
      month: { type: Boolean, default: false },    // a month only: "YYYY-MM"
   },
   emits: ["update:modelValue"],
   data() {
      return { autoId: "date-field-" + ++nextId, focused: false, isOpen: false, text: "", menuStyle: { visibility: "hidden" } };
   },
   computed: {
      inputId() {
         return this.id || this.autoId;
      },
      formStyle() {
         return this.$formStyle || "icons";
      },
      floating() {
         return this.formStyle === "floating" && !!this.label && !this.compact;
      },
      showIcon() {
         return this.formStyle === "icons" && !!this.icon && !this.compact;
      },
      placeholderText() {
         if (this.placeholder) return this.placeholder;
         if (this.month) return "mm/yyyy";
         return this.withTime ? "dd/mm/yyyy hh:mm" : "dd/mm/yyyy";
      },
      // "2026-10-29", or with time "2026-10-29 14:30" (also accepts "2026-10-29 14:30:00" / "2026-10-29T14:30"
      // from the database). A date without a time gets 00:00. Not a date: ""
      isoValue() {
         if (this.month) {
            const m = String(this.modelValue ?? "").match(/^(\d{4})-(\d{2})/);
            return m ? `${m[1]}-${m[2]}` : "";
         }
         const match = String(this.modelValue ?? "").match(/^(\d{4}-\d{2}-\d{2})(?:[ T](\d{2}):(\d{2}))?/);
         if (!match) return "";
         if (!this.withTime) return match[1];
         return `${match[1]} ${match[2] ?? "00"}:${match[3] ?? "00"}`;
      },
      // How many characters are compared with min / max: "YYYY-MM" (month) or "YYYY-MM-DD"
      rangeLength() {
         return this.month ? 7 : 10;
      },
      minIso() {
         return (this.min === "today" ? todayIso() : this.min).slice(0, this.rangeLength);
      },
      maxIso() {
         return (this.max === "today" ? todayIso() : this.max).slice(0, this.rangeLength);
      },
      // What the calendar gets: the month picker wants { month: 0-11, year }
      pickerValue() {
         if (!this.month) return this.isoValue;
         if (!this.isoValue) return null;
         const [y, m] = this.isoValue.split("-").map(Number);
         return { month: m - 1, year: y };
      },
      minDate() {
         return this.minIso ? this.toDate(this.minIso) : null;
      },
      maxDate() {
         return this.maxIso ? this.toDate(this.maxIso) : null;
      },
      todayBlocked() {
         return !this.inRange(todayIso()); // with time: only the day is checked
      },
      wrapClass() {
         if (this.showIcon) return "input-group";
         if (this.floating) {
            return { "sa-float": true, "is-up": this.focused || this.isOpen || this.text !== "", "is-focused": this.focused || this.isOpen };
         }
         return "";
      },
   },
   watch: {
      // Set from outside (Edit, Sample, Reset): show it as dd/mm/yyyy
      isoValue: {
         immediate: true,
         handler(value) {
            this.text = this.display(value);
         },
      },
   },
   mounted() {
      // Click anywhere else: close the calendar
      this.onOutside = (event) => {
         if (!this.isOpen) return;
         const inField = this.$refs.field && this.$refs.field.contains(event.target);
         const inMenu = this.$refs.menu && this.$refs.menu.contains(event.target);
         if (!inField && !inMenu) this.close();
      };
      document.addEventListener("mousedown", this.onOutside);
      // Page or window scrolled / resized while open: keep the calendar next to the box
      this.onMove = () => this.placeMenu();
   },
   beforeUnmount() {
      document.removeEventListener("mousedown", this.onOutside);
      window.removeEventListener("scroll", this.onMove, true);
      window.removeEventListener("resize", this.onMove);
   },
   methods: {
      toDate(iso) {
         const [y, m, d] = iso.split("-").map(Number);
         return new Date(y, m - 1, d || 1);
      },
      // "2026-10-29" -> "29/10/2026",  "2026-10-29 14:30" -> "29/10/2026 14:30"
      display(iso) {
         if (!iso) return "";
         if (this.month) {
            const [y, m] = iso.split("-");
            return `${m}/${y}`; // "10/2026"
         }
         const [date, time] = iso.split(" ");
         const [y, m, d] = date.split("-");
         return `${d}/${m}/${y}` + (time ? ` ${time}` : "");
      },
      // "29/10/2026" (also - or .) -> "2026-10-29". With time: "29/10/2026 14:30" -> "2026-10-29 14:30"
      // (no time typed = 00:00). null if it isn't a real date / time
      parse(text) {
         if (this.month) {
            // "10/2026" (also - or .) -> "2026-10"
            const mm = String(text).trim().match(/^(\d{1,2})[/.-](\d{4})$/);
            if (!mm || Number(mm[1]) < 1 || Number(mm[1]) > 12) return null;
            return `${mm[2]}-${pad(Number(mm[1]))}`;
         }
         const match = String(text).trim().match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/);
         if (!match) return null;
         const [d, m, y] = [Number(match[1]), Number(match[2]), Number(match[3])];
         const date = new Date(y, m - 1, d);
         if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null; // e.g. 31/02
         const iso = `${y}-${pad(m)}-${pad(d)}`;
         if (!this.withTime) return match[4] === undefined ? iso : null;
         const hours = Number(match[4] ?? 0);
         const minutes = Number(match[5] ?? 0);
         if (hours > 23 || minutes > 59) return null;
         return `${iso} ${pad(hours)}:${pad(minutes)}`;
      },
      // Only the day (or the month) is compared with min / max
      inRange(value) {
         const iso = String(value).slice(0, this.rangeLength);
         if (this.minIso && iso < this.minIso) return false;
         if (this.maxIso && iso > this.maxIso) return false;
         return true;
      },
      open() {
         if (this.$refs.input?.disabled || this.$refs.input?.readOnly) return;
         if (this.isOpen) return;
         this.isOpen = true;
         this.menuStyle = { visibility: "hidden" }; // measured first, then shown in place
         this.$nextTick(() => {
            this.makeMenuSolid();
            this.placeMenu();
         });
         window.addEventListener("scroll", this.onMove, true);
         window.addEventListener("resize", this.onMove);
      },
      // Under the box; above it when there's no room below. Never off the edge of the screen
      placeMenu() {
         const box = this.$refs.box;
         const menu = this.$refs.menu;
         if (!box || !menu) return;
         const rect = box.getBoundingClientRect();
         const height = menu.offsetHeight;
         const width = menu.offsetWidth;
         let top = rect.bottom + 4;
         if (top + height > window.innerHeight - 8 && rect.top - height - 4 > 8) top = rect.top - height - 4;
         const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
         this.menuStyle = { position: "fixed", top: `${top}px`, left: `${left}px` };
      },
      // Glass themes give dropdowns a see-through background. The month, year and time lists open
      // on top of the days, so they need a solid one: the theme's dropdown colour, made fully solid.
      makeMenuSolid() {
         const menu = this.$refs.menu;
         if (!menu) return;
         const parts = (getComputedStyle(menu).backgroundColor.match(/[\d.]+/g) || []).map(Number);
         let solid = "var(--bs-body-bg, #fff)"; // no colour at all: the page background
         if (parts.length >= 3 && !(parts.length === 4 && parts[3] === 0)) {
            solid = `rgb(${parts[0]}, ${parts[1]}, ${parts[2]})`;
         }
         menu.style.setProperty("--sa-date-solid-bg", solid);
         menu.dataset.solid = "1";
      },
      close() {
         this.isOpen = false;
         window.removeEventListener("scroll", this.onMove, true);
         window.removeEventListener("resize", this.onMove);
      },
      toggle() {
         if (this.isOpen) {
            this.close();
         } else {
            this.$refs.input?.focus();
            this.open();
         }
      },
      // Saves the date and tells the page (its @input / @change run, so the box turns green / red)
      setValue(iso) {
         this.text = this.display(iso);
         this.$emit("update:modelValue", iso);
         this.$nextTick(() => {
            const input = this.$refs.input;
            if (!input) return;
            input.value = this.text;
            input.dispatchEvent(new Event("input", { bubbles: true }));
            input.dispatchEvent(new Event("change", { bubbles: true }));
         });
      },
      // A day (or a time) chosen in the calendar. Date only: close. With time: stay open for the time (Done closes)
      pick(value) {
         // The month picker gives { month: 0-11, year }
         let iso = value || "";
         if (this.month && value && typeof value === "object") iso = `${value.year}-${pad(value.month + 1)}`;
         this.setValue(iso);
         if (!this.withTime || this.month) this.close();
      },
      pickToday() {
         if (this.month) {
            this.setValue(todayIso().slice(0, 7));
            this.close();
            return;
         }
         this.setValue(this.withTime ? `${todayIso()} ${nowTime()}` : todayIso());
         this.close();
      },
      clear() {
         this.setValue("");
         this.close();
      },
      // Typing: the slashes (and the space and : for the time) are added for you
      //   29102026 -> 29/10/2026      291020261430 -> 29/10/2026 14:30
      onType(event) {
         let value = event.target.value.replace(this.withTime ? /[^\d/.\-: ]/g : /[^\d/.-]/g, "");
         const digits = value.replace(/\D/g, "");
         if (this.month && !/[/.-]/.test(value) && digits.length > 2) {
            value = digits.slice(0, 2) + "/" + digits.slice(2, 6); // 102026 -> 10/2026
         } else if (!this.month && !/[/.\-: ]/.test(value) && digits.length > 2) {
            value = digits.slice(0, 2) + "/" + digits.slice(2, 4) + (digits.length > 4 ? "/" + digits.slice(4, 8) : "");
            if (this.withTime && digits.length > 8) {
               value += " " + digits.slice(8, 10) + (digits.length > 10 ? ":" + digits.slice(10, 12) : "");
            }
         }
         this.text = value;
         event.target.value = value;

         // A complete, real date in range: save it at once
         const iso = this.parse(value);
         if (iso && this.inRange(iso) && iso !== this.isoValue) this.$emit("update:modelValue", iso);
         if (value === "" && this.isoValue) this.$emit("update:modelValue", "");
      },
      onEnter() {
         this.onBlur();
         this.close();
      },
      // Leaving the box: a date that isn't real (or out of range) goes back to the last good one
      onBlur() {
         const iso = this.parse(this.text);
         if (this.text === "") {
            if (this.isoValue) this.setValue("");
         } else if (!iso || !this.inRange(iso)) {
            this.text = this.display(this.isoValue);
            if (this.$refs.input) this.$refs.input.value = this.text;
         } else {
            this.text = this.display(iso);
         }
      },
   },
};
</script>

<style>
/* The calendar under a DateField: takes the theme's dropdown colours */
.sa-date-menu {
   position: fixed;
   margin: 0;
   padding: 0.25rem;
   z-index: 1070; /* above windows (Bootstrap modals are 1055) */
   min-width: auto;
}
.sa-date-menu .dp__main,
.sa-date-menu .dp__menu {
   font-family: inherit;
}
.sa-date-menu .dp__theme_light,
.sa-date-menu .dp__theme_dark {
   --dp-background-color: var(--sa-date-solid-bg, transparent); /* solid: month / year / time lists cover the days */
   --dp-text-color: currentColor;
   --dp-hover-color: rgba(127, 127, 127, 0.2);
   --dp-hover-text-color: currentColor;
   --dp-hover-icon-color: currentColor;
   --dp-primary-color: var(--bs-primary, #0d6efd);
   --dp-primary-text-color: #fff;
   --dp-secondary-color: rgba(127, 127, 127, 0.65);
   --dp-border-color: transparent;
   --dp-menu-border-color: transparent;
   --dp-border-color-hover: transparent;
   --dp-icon-color: currentColor;
   --dp-disabled-color: transparent;
   --dp-disabled-color-text: rgba(127, 127, 127, 0.45);
   --dp-scroll-bar-background: transparent;
   --dp-highlight-color: rgba(var(--bs-primary-rgb, 13, 110, 253), 0.15);
}
.sa-date-menu .dp__menu {
   border: 0;
}
.sa-date-menu[data-solid] {
   background-color: var(--sa-date-solid-bg); /* the same solid colour all round (set once the theme's colour is read) */
}
.sa-date-menu .dp__overlay,
.sa-date-menu .dp__overlay_container {
   background: var(--sa-date-solid-bg, var(--bs-body-bg, #fff));
}
/* Today: a ring in the theme colour */
.sa-date-menu .dp__today {
   border-color: var(--bs-primary, #0d6efd);
}
.sa-date-icon {
   cursor: pointer;
}
</style>